/**
 * 디바이스 safe-area(노치·다이내믹 아일랜드·홈 인디케이터 등) inset을 게임 world 좌표로
 * 변환하고, UI 요소가 안전 영역 안에 "보이는지"를 정적으로 검증하는 순수 코어.
 *
 * DOM 의존 0 — inset 읽기는 {@link SafeAreaSource} Port로 추상화한다(어댑터는 소비 측 앱에
 * 위치, DIP). 시각 확인 없이 디바이스×UI 가시성을 단위테스트로 게이트하기 위한 패키지.
 *
 * 좌표계 전제: 게임은 design(world) 해상도로 레이아웃하고 Phaser FIT으로 화면에 맞춘다.
 * canvas 표시 px = world × {@link computeFitScale}. safe-area inset은 디바이스가 CSS px로
 * 주므로 world로 환산해야 같은 좌표계에서 UI rect와 비교할 수 있다.
 */

export interface SafeAreaInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** 너비·높이로 정의되는 2D 크기. 화면(screen) 또는 design(world) 해상도에 사용. */
export interface Size {
  width: number;
  height: number;
}

/** 위치와 크기로 정의되는 2D 직사각형. world(design) 좌표계 기준. */
export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** 노치/홈 인디케이터가 없는 환경(데스크톱·iPad mini 본문 등)의 0 inset. */
export const ZERO_INSETS: SafeAreaInsets = { top: 0, right: 0, bottom: 0, left: 0 };

/**
 * Phaser FIT 스케일 — design(world) 크기를 screen에 aspect 유지하며 맞출 때의 배율.
 * canvas 표시 px = world × scale. 두 축 중 작은 배율로 fit(넘치지 않게, 반대 축은 letterbox).
 */
export function computeFitScale(screen: Size, design: Size): number {
  return Math.min(screen.width / design.width, screen.height / design.height);
}

/**
 * CSS px inset → world 단위. canvas 표시 px = world × scale 이므로 1 CSS px = 1/scale world.
 * scale ≤ 0(미마운트 순간 등)이면 0 분모를 피해 ZERO를 돌려준다.
 */
export function toWorldInsets(cssInsets: SafeAreaInsets, canvasScale: number): SafeAreaInsets {
  if (canvasScale <= 0) return { ...ZERO_INSETS };
  return {
    top: cssInsets.top / canvasScale,
    right: cssInsets.right / canvasScale,
    bottom: cssInsets.bottom / canvasScale,
    left: cssInsets.left / canvasScale,
  };
}

/**
 * screen + design + CSS inset → world 단위 inset 한 번에(FIT 가정). 소비 측이 매번
 * FIT 스케일을 직접 구하지 않도록 묶은 편의 함수.
 */
export function resolveWorldInsets(
  screen: Size,
  design: Size,
  cssInsets: SafeAreaInsets,
): SafeAreaInsets {
  return toWorldInsets(cssInsets, computeFitScale(screen, design));
}

/** design(world) 영역에서 insets를 뺀 가용 Rect(world 좌표). 모든 UI는 이 안에 들어와야 한다. */
export function computeSafeRect(design: Size, insets: SafeAreaInsets): Rect {
  return {
    x: insets.left,
    y: insets.top,
    width: design.width - insets.left - insets.right,
    height: design.height - insets.top - insets.bottom,
  };
}

export interface NamedRect extends Rect {
  /** 위반 보고용 식별자 (예: "tabBar", "hud", "gameview"). */
  name: string;
}

export type RectEdge = "top" | "right" | "bottom" | "left";

export interface Violation {
  name: string;
  edge: RectEdge;
  /** safe 경계 밖으로 넘친 양(world px, 양수). */
  overflow: number;
}

/**
 * 각 rect의 4변이 safeRect 안에 있는지 검사한다. 넘친 변마다 {@link Violation} 1건.
 * 반환이 빈 배열이면 "모든 요소의 모든 꼭지점이 안전 영역 안" = 디바이스 가시성 PASS.
 *
 * @param epsilon 서브픽셀 넘침 허용폭(기본 0.5px). FIT 잔차(소수점)를 violation으로 오탐하지 않게.
 */
export function findVisibilityViolations(
  rects: readonly NamedRect[],
  safeRect: Rect,
  epsilon = 0.5,
): Violation[] {
  const out: Violation[] = [];
  const sLeft = safeRect.x;
  const sTop = safeRect.y;
  const sRight = safeRect.x + safeRect.width;
  const sBottom = safeRect.y + safeRect.height;
  for (const r of rects) {
    const right = r.x + r.width;
    const bottom = r.y + r.height;
    if (r.x < sLeft - epsilon) out.push({ name: r.name, edge: "left", overflow: sLeft - r.x });
    if (r.y < sTop - epsilon) out.push({ name: r.name, edge: "top", overflow: sTop - r.y });
    if (right > sRight + epsilon) out.push({ name: r.name, edge: "right", overflow: right - sRight });
    if (bottom > sBottom + epsilon)
      out.push({ name: r.name, edge: "bottom", overflow: bottom - sBottom });
  }
  return out;
}

export interface OverlapViolation {
  readonly a: string;
  readonly b: string;
}

/**
 * 두 사각 영역의 겹침 여부. 경계 접촉은 겹침이 아니다.
 * 레이아웃 함수 출력의 형제 UI 영역 침범을 단위테스트로 검증하는 공통 contract다.
 */
export function rectsOverlap(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width &&
    b.x < a.x + a.width &&
    a.y < b.y + b.height &&
    b.y < a.y + a.height
  );
}

/**
 * NamedRect 목록에서 겹치는 모든 쌍을 반환한다.
 * UI layout 테스트는 이 결과가 []임을 검증해 요소 간 침범을 고정한다.
 */
export function findOverlapViolations(rects: readonly NamedRect[]): OverlapViolation[] {
  const out: OverlapViolation[] = [];
  for (let i = 0; i < rects.length; i++) {
    for (let j = i + 1; j < rects.length; j++) {
      const a = rects[i]!;
      const b = rects[j]!;
      if (rectsOverlap(a, b)) out.push({ a: a.name, b: b.name });
    }
  }
  return out;
}

/**
 * inset 읽기 Port — DOM 어댑터(getComputedStyle / `--dvp-safe-*` / env(safe-area-inset-*))는
 * 소비 측(앱)에 둔다(DIP). 패키지는 이 인터페이스만 알고 DOM을 모른다.
 */
export interface SafeAreaSource {
  read(): SafeAreaInsets;
}
